import React from 'react';
import { Menu } from 'primereact/menu';
import { classNames } from 'primereact/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.scss';

interface SidebarProps {
    visible?: boolean;
    onHide?: () => void;
}

const iconMap: Record<string, string> = {
    dashboard: '/sidebar/house-sidebar.svg',
    fleet: '/sidebar/car-sidebar.svg',
    offer: '/sidebar/offer-sidebar.svg',
    orders: '/sidebar/orders-sidebar.svg',
    transactions: '/sidebar/transactions-sidebar.svg',
    documents: '/sidebar/documents-sidebar.svg',
    contact: '/sidebar/contact-sidebar.svg',
};

const Sidebar: React.FC<SidebarProps> = ({ visible = true, onHide }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const sidebarRef = React.useRef<HTMLDivElement>(null);

    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    React.useEffect(() => {
        if (!isMobile || !visible) return;

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                if (onHide) onHide();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMobile, visible, onHide]);

    const itemRenderer = (item: any, options: any) => (
        <div
            className={classNames('p-menuitem-content', { 'active-menuitem': location.pathname === item.path })}
            onClick={() => {
                if (item.path) navigate(item.path);
                if (onHide) onHide();
            }}
            style={{ cursor: item.path ? 'pointer' : 'default' }}
        >
            {/* eslint-disable-next-line */}
            <a className="flex align-items-center p-menuitem-link menu-text label"> 
                {item.iconSrc && (
                    <img
                        className="menu-text icon"
                        src={item.iconSrc}
                        alt="sidebar-icon"
                        style={{ marginRight: '1rem', width: 20, height: 20, objectFit: 'contain' }}
                    />
                )}
                <span className="menu-text">{item.label}</span>
            </a>
        </div>
    );

    const items = [
        {
            template: () => (
                <img alt='mooveno-logo' className="logo" src={`/sidebar/mooveno-logo.svg`} style={{cursor: 'pointer'}} onClick={() => navigate('/')} />
            ),
        },
        {
            id: 'sidebar_menu_1',
            label: 'Strona główna',
            iconSrc: iconMap.dashboard,
            path: '/',
            template: itemRenderer,
        },
        {
            id: 'sidebar_menu_2',
            label: 'Flota',
            iconSrc: iconMap.fleet,
            path: '/fleet/',
            template: itemRenderer,
        },
        {
            id: 'sidebar_menu_3',
            label: 'Twoja oferta',
            iconSrc: iconMap.offer,
            path: '/offer/',
            template: itemRenderer,
        },
        {
            id: 'sidebar_menu_4',
            label: 'Historia usług',
            iconSrc: iconMap.orders,
            path: '/orders/',
            template: itemRenderer,
        },
        {
            id: 'sidebar_menu_5',
            label: 'Transakcje',
            iconSrc: iconMap.transactions,
            path: '/transactions/',
            template: itemRenderer,
        },
        {
            id: 'sidebar_menu_6',
            label: 'Dokumenty',
            iconSrc: iconMap.documents,
            path: '/documents/',
            template: itemRenderer,
        },
        {
            id: 'sidebar_menu_7',
            label: 'Kontakt',
            iconSrc: iconMap.contact,
            path: '/contact/',
            template: itemRenderer,
        },
    ];

    const sidebarStyle: React.CSSProperties = isMobile
        ? {
            minHeight: '100vh',
            background: '#14213d',
            color: '#fff',
            padding: '0',
            width: 220,
            position: 'fixed',
            left: visible ? 0 : -220,
            top: 0,
            zIndex: 1050,
            transition: 'left 0.3s',
            boxShadow: visible ? '2px 0 8px rgba(0,0,0,0.15)' : 'none',
        }
        : {
            minHeight: '100vh',
            background: '#14213d',
            color: '#fff',
            padding: '0',
            width: 220,
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 1050,
            transition: 'none',
        };

    return (
        <div
            ref={sidebarRef}
            className={classNames('sidebar', { 'sidebar-mobile': isMobile })}
            style={sidebarStyle}
        >
            <Menu model={items} className="w-full md:w-15rem border-none bg-transparent" style={{ border: '0' }} />
        </div>
    );
};

export default Sidebar;